import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
} from "@/components/catalyst/dropdown";
import { Badge } from "@/components/catalyst/badge";
import { NavbarItem } from "@/components/catalyst/navbar";
import { SidebarItemButton } from "@/components/catalyst/sidebar";
import { LanguageIcon } from "@heroicons/react/20/solid";
import { Locale, SUPPORTED_LOCALES, useLocaleStore } from "./useLocale";
import { CheckIcon, ChevronDownIcon } from "@heroicons/react/16/solid";

export function LanguageSelector() {
  const { locale, setLocale } = useLocaleStore();

  return (
    <Dropdown>
      <DropdownButton as={NavbarItem} aria-label="Select language">
        <LanguageIcon className="h-5 w-5" />
      </DropdownButton>
      <DropdownMenu className="min-w-48" anchor="bottom end">
        {Object.entries(SUPPORTED_LOCALES).map(([key, { name, flag }]) => (
          <DropdownItem
            key={key}
            onClick={() => setLocale(key as Locale)}
            className={locale === key ? "bg-zinc-100" : ""}
          >
            <DropdownLabel>
              {name} {flag}
            </DropdownLabel>
          </DropdownItem>
        ))}
      </DropdownMenu>
    </Dropdown>
  );
}

export function LanguageSelectorInline() {
  const { locale, setLocale } = useLocaleStore();

  return (
    <>
      {Object.entries(SUPPORTED_LOCALES).map(([key, { name, flag }]) => (
        <DropdownItem key={key} onClick={() => setLocale(key as Locale)}>
          {locale === key ? <CheckIcon /> : <div className="size-4" />}
          <DropdownLabel>
            {name} {flag}
          </DropdownLabel>
          {key !== "en" ? (
            <Badge color="yellow" className="col-start-5">
              In Progress
            </Badge>
          ) : null}
        </DropdownItem>
      ))}
    </>
  );
}

export function LanguageSelectorHeader() {
  const { locale, setLocale } = useLocaleStore();
  const currentLanguage = SUPPORTED_LOCALES[locale];

  return (
    <div className="relative">
      <Dropdown>
        <DropdownButton
          as="button"
          aria-label="Select language"
          title={`Language: ${currentLanguage.name}`}
          className="flex items-center gap-1.5 rounded-full border border-zinc-700 bg-zinc-800/50 px-2 py-1 text-xs font-medium text-zinc-300 transition-colors hover:border-zinc-600 hover:bg-zinc-800"
        >
          <span className="text-sm leading-none">{currentLanguage.flag}</span>
          <span className="uppercase">{locale}</span>
          <ChevronDownIcon className="size-3 shrink-0 text-zinc-500" />
        </DropdownButton>
        <DropdownMenu className="min-w-48" anchor="bottom end">
          {Object.entries(SUPPORTED_LOCALES).map(([key, { name, flag }]) => (
            <DropdownItem
              key={key}
              onClick={() => setLocale(key as Locale)}
              className={locale === key ? "bg-zinc-100" : ""}
            >
              <DropdownLabel>
                {flag} {name}
              </DropdownLabel>
            </DropdownItem>
          ))}
        </DropdownMenu>
      </Dropdown>
    </div>
  );
}

export function LanguageSelectorSidebar() {
  const { locale, setLocale } = useLocaleStore();
  const currentLanguage = SUPPORTED_LOCALES[locale];

  return (
    <Dropdown>
      <DropdownButton as={SidebarItemButton} aria-label="Select language">
        <span className="truncate text-xs text-zinc-400">
          {currentLanguage.name} {currentLanguage.flag}
        </span>
        <LanguageIcon className="ml-auto h-4 w-4 text-zinc-400" />
      </DropdownButton>
      <DropdownMenu className="min-w-48" anchor="top start">
        {Object.entries(SUPPORTED_LOCALES).map(([key, { name, flag }]) => (
          <DropdownItem
            key={key}
            onClick={() => setLocale(key as Locale)}
            className={locale === key ? "bg-zinc-100" : ""}
          >
            <DropdownLabel>
              {name} {flag}
            </DropdownLabel>
          </DropdownItem>
        ))}
      </DropdownMenu>
    </Dropdown>
  );
}
